import { useEffect, useState } from "react";
import { Plus, Trash2, Mail, Copy, UserPlus, Clock, XCircle, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const ROLES = [
  { value: "org_admin", label: "Org admin" },
  { value: "hr_manager", label: "HR manager" },
  { value: "sales_ops", label: "Sales / Ops" },
  { value: "sender", label: "Sender" },
  { value: "approver", label: "Approver" },
  { value: "viewer", label: "Viewer" },
];

export default function Team() {
  const { user } = useAuth();
  const isAdmin = user?.role === "org_admin" || user?.role === "super_admin";

  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "sender" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [m, i] = await Promise.all([
        api.get("/org/members"),
        api.get("/invitations").catch(() => ({ data: [] })),
      ]);
      setMembers(m.data);
      setInvites(i.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const invite = async () => {
    if (!form.email || !form.name) return toast.error("Name and email are required");
    setBusy(true);
    try {
      await api.post("/invitations", form);
      toast.success("Invitation created");
      setOpen(false);
      setForm({ name: "", email: "", role: "sender" });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id) => {
    if (!confirm("Revoke this invitation?")) return;
    try {
      await api.delete(`/invitations/${id}`);
      toast.success("Revoked");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (id) => {
    if (!confirm("Remove this member from the organization?")) return;
    try {
      await api.delete(`/org/members/${id}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const copyLink = (url) => {
    navigator.clipboard.writeText(url);
    toast.success("Invite link copied");
  };

  const pending = invites.filter((i) => i.status === "pending");
  const historical = invites.filter((i) => i.status !== "pending");

  return (
    <div className="p-6 lg:p-10 max-w-6xl mx-auto" data-testid="team-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-medium">
            Team
          </div>
          <h1 className="mt-1 text-3xl font-bold tracking-tight">People &amp; roles</h1>
          <p className="mt-1 text-sm text-slate-500">
            Invite teammates via secure links. Roles are enforced across documents and
            settings.
          </p>
        </div>
        {isAdmin && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
                data-testid="invite-open-btn"
              >
                <UserPlus className="h-4 w-4" /> Invite teammate
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Send an invitation</DialogTitle>
              </DialogHeader>
              <p className="text-xs text-slate-500">
                They'll receive a secure invitation link that expires in 7 days. They can
                set their own password when they accept.
              </p>
              <div className="space-y-3 mt-2">
                <div>
                  <Label>Name</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="mt-1.5 h-10"
                    data-testid="invite-name-input"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="mt-1.5 h-10"
                    data-testid="invite-email-input"
                  />
                </div>
                <div>
                  <Label>Role</Label>
                  <Select
                    value={form.role}
                    onValueChange={(v) => setForm({ ...form, role: v })}
                  >
                    <SelectTrigger className="mt-1.5 h-10" data-testid="invite-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ROLES.map((r) => (
                        <SelectItem key={r.value} value={r.value}>
                          {r.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={invite}
                  disabled={busy}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="invite-send-btn"
                >
                  {busy ? "Creating…" : "Create invitation"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Tabs defaultValue="members" className="mt-8">
        <TabsList>
          <TabsTrigger value="members" data-testid="team-tab-members">
            Members ({members.length})
          </TabsTrigger>
          <TabsTrigger value="pending" data-testid="team-tab-pending">
            Pending invites ({pending.length})
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="team-tab-history">
            History
          </TabsTrigger>
        </TabsList>

        <TabsContent value="members">
          <div className="mt-4 rounded-xl border border-slate-200 bg-white overflow-hidden">
            <div className="grid grid-cols-[1fr_180px_140px_60px] px-5 py-3 border-b border-slate-200 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 font-medium">
              <div>Member</div>
              <div>Role</div>
              <div>Joined</div>
              <div></div>
            </div>
            {members.map((m) => (
              <div
                key={m.id}
                className="grid grid-cols-[1fr_180px_140px_60px] items-center px-5 py-4 border-t border-slate-100"
                data-testid={`member-row-${m.id}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-9 w-9 rounded-full bg-slate-900 text-white text-xs font-semibold flex items-center justify-center">
                    {m.name.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{m.name}</div>
                    <div className="text-xs text-slate-500 truncate">{m.email}</div>
                  </div>
                </div>
                <div className="text-sm capitalize">{m.role.replace(/_/g, " ")}</div>
                <div className="text-xs font-mono text-slate-500">
                  {new Date(m.created_at).toLocaleDateString()}
                </div>
                <div className="text-right">
                  {isAdmin && m.id !== user?.id && (
                    <button
                      onClick={() => remove(m.id)}
                      className="p-2 rounded hover:bg-slate-100 text-red-600"
                      data-testid={`member-remove-${m.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="pending">
          <div className="mt-4 rounded-xl border border-slate-200 bg-white overflow-hidden">
            {pending.length === 0 && (
              <div className="p-10 text-center">
                <Mail className="h-8 w-8 text-slate-400 mx-auto" />
                <div className="mt-3 text-sm font-medium">No pending invitations</div>
                <div className="text-xs text-slate-500 mt-1">
                  Invite a teammate and track their status here.
                </div>
              </div>
            )}
            {pending.map((i) => (
              <div
                key={i.id}
                className="grid grid-cols-[1fr_140px_160px_auto] items-center px-5 py-4 border-b border-slate-100 last:border-0"
                data-testid={`invite-row-${i.id}`}
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium">{i.name || i.email}</div>
                  <div className="text-xs text-slate-500 truncate">{i.email}</div>
                  <div className="mt-1 text-[10px] text-slate-400 font-mono truncate">
                    {i.accept_url}
                  </div>
                </div>
                <div className="text-sm capitalize">{i.role.replace(/_/g, " ")}</div>
                <div className="text-xs text-slate-500 flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  expires {new Date(i.expires_at).toLocaleDateString()}
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyLink(i.accept_url)}
                    className="h-8 text-xs"
                    data-testid={`invite-copy-${i.id}`}
                  >
                    <Copy className="h-3 w-3 mr-1" /> Copy link
                  </Button>
                  {isAdmin && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => revoke(i.id)}
                      className="h-8 text-xs text-red-600 border-red-200 hover:bg-red-50"
                      data-testid={`invite-revoke-${i.id}`}
                    >
                      Revoke
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="history">
          <div className="mt-4 rounded-xl border border-slate-200 bg-white overflow-hidden">
            {historical.length === 0 && (
              <div className="p-10 text-center text-sm text-slate-500">
                No past invitations yet.
              </div>
            )}
            {historical.map((i) => (
              <div
                key={i.id}
                className="grid grid-cols-[1fr_140px_140px_120px] items-center px-5 py-3 border-b border-slate-100 last:border-0"
              >
                <div>
                  <div className="text-sm font-medium">{i.email}</div>
                  <div className="text-xs text-slate-500 capitalize">
                    role {i.role.replace(/_/g, " ")}
                  </div>
                </div>
                <div className="flex items-center gap-1 text-xs">
                  {i.status === "accepted" ? (
                    <>
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                      <span className="text-emerald-700 font-medium">Accepted</span>
                    </>
                  ) : i.status === "revoked" ? (
                    <>
                      <XCircle className="h-3.5 w-3.5 text-red-600" />
                      <span className="text-red-700 font-medium">Revoked</span>
                    </>
                  ) : (
                    <>
                      <Clock className="h-3.5 w-3.5 text-slate-500" />
                      <span className="text-slate-600 capitalize">{i.status}</span>
                    </>
                  )}
                </div>
                <div className="text-xs font-mono text-slate-500">
                  {new Date(i.created_at).toLocaleDateString()}
                </div>
                <div className="text-[10px] text-slate-400 text-right">
                  by {i.invited_by_name}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
